import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Loader2 } from 'lucide-react';
import api from '../../lib/api';
import { fmtCurrency } from '../../lib/localeUtils';

export const AssetDisposeDialog = ({ asset, onClose, onSaved }) => {
  const [form, setForm] = useState({ disposal_date: '', disposal_amount: '0', reason: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (asset) setForm({ disposal_date: new Date().toISOString().slice(0, 10), disposal_amount: '0', reason: '' });
  }, [asset]);

  const [nbvAtDate, setNbvAtDate] = useState(null);
  useEffect(() => {
    if (!asset || !form.disposal_date) return;
    api.get(`/assets/${asset.asset_id}`, { params: { as_of: form.disposal_date } })
      .then((r) => setNbvAtDate(r.data.net_book_value)).catch(() => setNbvAtDate(null));
  }, [asset, form.disposal_date]);
  const gain = nbvAtDate == null ? null : Number(form.disposal_amount || 0) - nbvAtDate;

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/assets/${asset.asset_id}/dispose`, { ...form, disposal_amount: Number(form.disposal_amount || 0) });
      const gl = r.data.gain_loss;
      toast.success(`Asset disposed — ${gl >= 0 ? 'gain' : 'loss'} of ${fmtCurrency(Math.abs(gl))}`);
      onClose(); onSaved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Disposal failed');
    } finally { setSaving(false); }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <Dialog open={!!asset} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="asset-dispose-dialog">
        <DialogHeader><DialogTitle>Dispose {asset?.name}</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground" data-testid="asset-dispose-nbv">
          Book value on disposal date: {nbvAtDate == null ? '…' : fmtCurrency(nbvAtDate)}
          {gain != null && <span className={gain >= 0 ? 'text-emerald-600' : 'text-red-600'}> · {gain >= 0 ? 'gain' : 'loss'} {fmtCurrency(Math.abs(gain))}</span>}
        </p>
        <div className="space-y-3">
          <div><Label className="text-xs">Disposal date</Label><Input type="date" value={form.disposal_date} onChange={set('disposal_date')} data-testid="asset-dispose-date" /></div>
          <div><Label className="text-xs">Sale proceeds (0 if scrapped)</Label><Input type="number" min="0" step="0.01" value={form.disposal_amount} onChange={set('disposal_amount')} data-testid="asset-dispose-amount" /></div>
          <div><Label className="text-xs">Reason</Label><Input value={form.reason} onChange={set('reason')} placeholder="Sold, scrapped, stolen..." data-testid="asset-dispose-reason" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} variant="destructive" data-testid="asset-dispose-confirm">
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Dispose
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
